import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
} from '@mui/material';
import useTable, { getComparator, emptyRows } from 'src/hooks/useTable';
import Scrollbar from 'src/components/Scrollbar';
import { TableNoData, TableEmptyRows, TableHeadCustom } from 'src/components/table';
import { Item } from 'src/@types/Course';
import LoadingScreen from 'src/components/LoadingScreen';
import SectionTableToolBar from './SectionItemTableToolbar';
import useTabs from 'src/hooks/useTabs';
import SectionTableItemRow from './SectionItemTableRow';
import ConfirmationDialog from './ConformiationDialog';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
const TABLE_HEAD = [
  { id: 'title', label: 'Title', align: 'left' },
  { id: 'type', label: 'Type', align: 'left' },
  { id: 'points', label: 'points', align: 'left' },
  { id: 'duration', label: 'Duration', align: 'left' },
  { id: '' },
];

// ----------------------------------------------------------------------

export default function SectionsItemTable({
  items,
  onEdit,
  onDeleteItem,
  onGoUpDown,
}: {
  items: Item[];
  onEdit(item: Item, index: number): void;
  onDeleteItem(index: number): void;
  onGoUpDown: (fromIndex: number, toIndex: number) => void;
}) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const [loading, setLoading] = useState<boolean>(false);
  const [filterName, setFilterName] = useState('');
  const [tableData, setTableData] = useState<Item[]>([]);
  const [confirmOpen, setConfirmOpen] = useState<{
    open: boolean;
    currentId: number | undefined;
  }>({ open: false, currentId: undefined });
  const { currentTab: filterStatus } = useTabs('all');

  useEffect(() => {
    setTableData(items);
  }, [items]);
  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const isNotFound = !dataFiltered?.length;
  return (
    <>
      <Container
        sx={{
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
          maxWidth: 'unset !important',
        }}
      >
        <Card>
          <SectionTableToolBar filterName={filterName} onFilterName={handleFilterName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {loading ? (
                <LoadingScreen />
              ) : (
                <Table size={dense ? 'small' : 'medium'}>
                  <TableHeadCustom
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={items?.length}
                    onSort={onSort}
                  />

                  <TableBody>
                    {dataFiltered &&
                      dataFiltered?.length > 0 &&
                      dataFiltered
                        ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        ?.map((row, index) => (
                          <SectionTableItemRow
                            isFirst={index == 0}
                            isLast={index == dataFiltered.length - 1}
                            onGoUp={() => {
                              onGoUpDown(index, (index + 1));
                            }}
                            onGoDown={() => {
                              onGoUpDown(index, (index - 1));
                            }}
                            onEditRow={() => onEdit(row, index)}
                            key={row.order}
                            row={row}
                            selected={false}
                            onDeleteRow={() => {
                              setConfirmOpen({ open: true, currentId: index });
                            }}
                          />
                        ))}

                    <TableEmptyRows
                      height={2.2}
                      emptyRows={emptyRows(page, rowsPerPage, items?.length)}
                    />

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25]}
              component="div"
              count={items?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <ConfirmationDialog
        isOpen={confirmOpen.open}
        onClose={() => setConfirmOpen({ open: false, currentId: undefined })}
        confirmAction={() => onDeleteItem(confirmOpen.currentId as number)}
        loading={loading}
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
}: {
  tableData: Item[];
  comparator: (a: any, b: any) => number;
  filterName: string;
}) {
  const stabilizedThis = tableData?.map((el, index) => [el, index] as const);

  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis?.map((el) => el[0]);
  if (filterName) {
    tableData = tableData?.filter(
      (item: Record<string, any>) =>
        item.title.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  return tableData;
}
